import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
import { getErrorMessage } from "../../utils/posErrors";
import { copy } from "../../copy";
import { applyLoyaltyPoints, verifyRedeemOtp } from "../index";
import {
  OTP_RESEND_SECONDS,
  REDEEM_OTP,
  REDEMPTION_FAILED_KEY,
  VERIFICATION_FAILED_KEY,
} from "../constants";
import { classifyRedeemResponse } from "../redemption";
import { getRedeemOtpLength } from "../provider";
import { selectActiveLoyaltyProvider, selectCustomerPhone } from "../selectors";

/**
 * The redemption OTP step: the code, the resend countdown, and the verify call.
 *
 * Mounted only once the backend has actually asked for an OTP —
 * `useLoyaltyRedemption().otpSession.isOpen`. It does NOT redeem on mount; the redemption
 * already happened, and `resend` re-issues it deliberately (that is what the backend does with
 * a resend).
 *
 * ## Both actions are guarded, and both report a failure
 *
 * `verify` and `resend` each hold the same in-flight slot, so neither can be double-submitted
 * and neither can run while the other is outstanding — see `inFlight`. This came late: the
 * sibling hook got a ref guard and its own test suite while this dialog, one screen away, could
 * still send two `verifyRedeemOtp` calls for one sale on a double tap.
 *
 * Failures are reported rather than swallowed, for the same reason and by the same rule
 * (`classifyRedeemResponse`): a request failure reaches these callbacks as `undefined`, which
 * `resend` used to read `.sale` off — a `TypeError` on every failed resend — and which left
 * `verify` showing no message at all.
 *
 * @param points   the points being redeemed, from `otpSession.points`
 * @param onVerified called once the code is accepted and the sale is saved
 */
export function useRedeemPointsOtp({
  points,
  onVerified,
}: {
  points: number;
  onVerified: () => void;
}) {
  const dispatch = useDispatch();
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const customerPhone = useSelector(selectCustomerPhone);

  const [otp, setOtpRaw] = useState("");
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(OTP_RESEND_SECONDS);

  /** Counts down to zero and stops; zero is what enables "Resend". */
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  /**
   * A verify or a resend is in flight. ONE slot covers both.
   *
   * 🔴 A ref, not state, for the reason `useLoyaltyRedemption` uses one: `setIsBusy(true)` only
   * schedules a render, so it does not stop the handler already running or a second handler that
   * fires before React commits. Two taps on Confirm inside one batch both read `false` and both
   * send `verifyRedeemOtp` for the same sale. A ref flips synchronously, so the second caller
   * sees it; `isBusy` exists beside it only so a view can render the state.
   *
   * ⚠️ **Shared between the two actions deliberately.** They are not independent: a resend
   * REPLACES the code that a verify would be checking, so allowing one while the other is
   * outstanding races the customer's own digits against a fresh challenge. The countdown is not
   * this guard either — it throttles how often a code may be re-sent, and it is a state read
   * with exactly the batching hole described above.
   */
  const inFlight = useRef(false);
  const [isBusy, setIsBusy] = useState(false);

  /** Claim the slot. `false` means a request is already running; the caller must do nothing. */
  const beginRequest = useCallback(() => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setIsBusy(true);
    return true;
  }, []);

  /** Release it. Every path that calls `beginRequest` must reach this from its callback. */
  const endRequest = useCallback(() => {
    inFlight.current = false;
    setIsBusy(false);
  }, []);

  const setOtp = useCallback((value: string) => {
    setError("");
    const digits = String(value ?? "").replace(/[^0-9]/g, "");
    setOtpRaw(digits.slice(0, REDEEM_OTP.MAX_LENGTH));
  }, []);

  /**
   * Re-issue the redemption, which sends a fresh code.
   *
   * ⚠️ A failure here is reported but does not close the step — the previous code may still be
   * valid, and dropping the cashier out of the dialog would lose it.
   */
  const resend = useCallback(() => {
    if (!beginRequest()) return;
    setSecondsLeft(OTP_RESEND_SECONDS);
    setError("");
    applyLoyaltyPoints(points, (res: any) => {
      endRequest();
      /**
       * 🔴 Classified by the shared rule, not read field by field — this block used to test
       * `res?.code || res?.errorCode` and then reach for `res.sale`, which THREW on every
       * failed resend. A request failure arrives as `undefined`, not as a code, so neither
       * field was set and the `undefined.sale` read went straight into the saga's own `try`,
       * whose catch discards it. Identical to the bug `classifyRedeemResponse` was written for
       * in `useLoyaltyRedemption.redeem`; this was the second, missed instance.
       *
       * A resend re-issues the redemption, so its response is a redeem response and this is
       * the right reader for it. A repeated `challenge` still carries the sale and is saved —
       * the dialog stays open either way, which is what the note above says it must do.
       */
      const outcome = classifyRedeemResponse(res);
      if (outcome.kind === "failed") {
        setError(getErrorMessage(outcome.code) || copy(REDEMPTION_FAILED_KEY));
        return;
      }
      dispatch(saveSaleData(outcome.sale));
    });
  }, [points, dispatch, beginRequest, endRequest]);

  const verify = useCallback(() => {
    if (!beginRequest()) return;
    verifyRedeemOtp(otp, points, (res: any) => {
      // Before the branches: the success path calls `onVerified`, which closes the dialog and
      // unmounts this hook, so the slot has to be released while it still exists.
      endRequest();
      if (!res || res?.errorCode) {
        // `getErrorMessage` answers `undefined` for an absent code, and the old `|| ""` turned
        // that into no message at all — Confirm appeared to do nothing on a network failure.
        setError(getErrorMessage(res?.errorCode) || copy(VERIFICATION_FAILED_KEY));
        return;
      }
      dispatch(saveSaleData(res.sale));
      onVerified();
    });
  }, [otp, points, dispatch, onVerified, beginRequest, endRequest]);

  return {
    otp,
    setOtp,
    error,
    /** Zero means the countdown has run out; above zero it is the figure to show. */
    secondsLeft,
    /**
     * 🔴 `isBusy` is folded into BOTH of these rather than exposed for each view to remember.
     *
     * All three surfaces — the kiosk dialog and both POS ones — already gate their CTAs on
     * `canVerify` / `canResend`, so putting the in-flight state here disables Confirm and Resend
     * everywhere with no view change and nothing for a fourth surface to forget. `isBusy` is
     * returned as well, but only for a view that wants to draw progress; it is not what makes
     * the press safe. The ref inside `beginRequest` is.
     */
    canResend: secondsLeft <= 0 && !isBusy,
    resend,
    verify,
    canVerify: !!otp && otp.length >= REDEEM_OTP.MIN_LENGTH && !error && !isBusy,
    /** A verify or resend is in flight. For a spinner — the guard does not depend on it. */
    isBusy,
    /** How many digits this provider sends — for the prompt, not for validation. */
    otpLength: getRedeemOtpLength(activeProvider?.metaId),
    customerPhone,
  };
}
